import { assessmentTranslator } from './assessmentLanguage';
import { curriculumSourceParagraphs } from './assessmentDocument';
import { AlignmentType, Document, HeadingLevel, LevelFormat, SectionType } from 'docx';
import { paragraph, heading, markedParagraph, answerLines, grid, rule, assessmentStyles, assessmentPage, assessmentFooter, questionVisuals } from './assessmentDocument';


// `visuals` is the WeakMap from prepareQuestionVisuals; without it, tables still export but figures do not.
export function buildTestDocument(test, form = {}, visuals = new WeakMap()) {
    const translate = assessmentTranslator(test.outputLanguage || form.outputLanguage || 'English');
    const subject = test.subject || form.subject || translate('Assessment');
    const grade = test.grade || form.grade || '';
    const title = test.title || `${subject} ${translate('Test')}`;
    const footer = (label) => assessmentFooter(grade, subject, label, translate('Grade'));
    const page = assessmentPage;
    const coverField = (label) => paragraph(label, { border: { bottom: rule }, spacing: { before: 300, after: 240, line: 400 } });
    const instructions = (test.instructions || []).map((instruction) => paragraph(instruction, {
        numbering: { reference: 'instructions', level: 0 }, spacing: { after: 200 }, keepNext: false
    }));
    const learnerPaper = (test.sections || []).flatMap((section) => [
        heading(`${section.title} [${section.marks} ${translate('marks')}]`),
        ...(section.introText ? [paragraph(section.introText, { spacing: { after: 240 } })] : []),
        ...(section.questions || []).flatMap((question) => [
            markedParagraph(question.questionNumber, question.title, question.marks, { heading: HeadingLevel.HEADING_2, keepNext: true }),
            ...questionVisuals(question, visuals.get(question)),
            ...(question.items || []).flatMap((item) => {
                const options = item.options || [];
                const lines = answerLines(options.length ? 1 : item.answerSpaceLines);
                return [
                    markedParagraph(item.itemNumber, item.prompt, item.marks, { keepNext: options.length > 0 || lines.length > 0 }),
                    ...options.map((option, index) => paragraph(`${String.fromCharCode(65 + index)}. ${option}`, { indent: { left: 640 }, keepNext: true })),
                    ...lines
                ];
            })
        ])
    ]);
    const memorandum = (test.memorandum || []).flatMap((item) => [
        markedParagraph(item.itemNumber, translate('Answer / solution'), item.marks, { keepNext: true }),
        paragraph(item.solution, { indent: { left: 640 }, spacing: { after: 160 } }),
        paragraph(`${translate('Marking guideline')}: ${item.markingGuideline || ''}`, { indent: { left: 640 }, spacing: { after: 240 } })
    ]);

    return new Document({
        title,
        creator: 'Marksure',
        styles: assessmentStyles,
        numbering: { config: [{ reference: 'instructions', levels: [{ level: 0, format: LevelFormat.DECIMAL, text: '%1.', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 420, hanging: 420 } } } }] }] },
        sections: [
            {
                properties: { page },
                children: [
                    paragraph(translate('MARKSURE ASSESSMENT'), { alignment: AlignmentType.CENTER, spacing: { before: 500, after: 360 } }),
                    paragraph(title, { heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER }),
                    paragraph(`${translate('GRADE')} ${grade} | ${subject.toUpperCase()}`, { alignment: AlignmentType.CENTER }),
                    paragraph(`${translate('TERM')} ${test.term || form.term || 1}`, { alignment: AlignmentType.CENTER, spacing: { after: 600 } }),
                    paragraph(`${translate('MARKS')}: ${test.totalMarks}`),
                    paragraph(`${translate('TIME')}: ${test.duration || form.duration || ''}`, { spacing: { after: 300 } }),
                    ...[translate('SCHOOL NAME:'), translate('CLASS:'), translate('SURNAME:'), translate('NAME:'), translate('DATE:')].map(coverField),
                    paragraph(`${translate('MARKS OBTAINED')}: __________________ / ${test.totalMarks}`, { spacing: { before: 400 } })
                ]
            },
            {
                properties: { type: SectionType.NEXT_PAGE, page: { ...page, pageNumbers: { start: 1 } } },
                footers: { default: footer(translate('Learner paper')) },
                children: [
                    heading(translate('INSTRUCTIONS TO THE LEARNER')),
                    ...instructions,
                    paragraph(`${translate('Total')}: ${test.totalMarks} ${translate('marks')}. ${translate('Time')}: ${test.duration || form.duration || ''}.`, { spacing: { before: 240, after: 240 } }),
                    paragraph(translate('The test starts on the next page.'), { alignment: AlignmentType.CENTER, spacing: { before: 480 } }),
                    paragraph(translate('LEARNER TEST'), { heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
                    ...learnerPaper,
                    paragraph(`${translate('TOTAL')}: ${test.totalMarks} ${translate('MARKS')}`, { alignment: AlignmentType.RIGHT, spacing: { before: 360 } }),
                    paragraph(translate('END OF LEARNER PAPER'), { alignment: AlignmentType.CENTER, spacing: { before: 240 } })
                ]
            },
            {
                properties: { type: SectionType.NEXT_PAGE, page: { ...page, pageNumbers: { start: 1 } } },
                footers: { default: footer(translate('Memorandum')) },
                children: [
                    heading(translate('MEMORANDUM AND MARKING GUIDELINES')),
                    paragraph(`${title} | ${translate('Grade')} ${grade} | ${test.totalMarks} ${translate('marks')}`),
                    ...memorandum,
                    heading(translate('MARKING MATRIX'), true),
                    grid([translate('Question'), translate('Topic / focus'), translate('Cognitive level'), translate('Marks')], [
                        ...(test.markingGrid || []).map((item) => [item.itemNumber, item.focus, translate(item.cognitiveLevel), item.marks]),
                        [translate('Total'), '', '', test.totalMarks]
                    ], [1100, 4038, 3400, 1100]),
                    heading(`${translate(test.cognitiveFramework || 'Cognitive level')} ${translate('distribution')}`),
                    grid([translate('Cognitive level'), translate('Target'), translate('Marks'), translate('Actual')], (test.cognitiveDistribution || []).map((item) => [
                        translate(item.level),
                        item.targetPercentage ? `${item.targetPercentage}%` : '',
                        item.marks,
                        item.actualPercentage !== undefined ? `${item.actualPercentage}%` : ''
                    ]), [4438, 1700, 1700, 1800]),
                    ...(test.cognitiveFrameworkSource ? [paragraph(`${translate('Source')}: ${test.cognitiveFrameworkSource}`, { spacing: { before: 120 } })] : []),
                    heading(translate('TEACHER NOTES')),
                    ...(test.teacherNotes || []).map((note) => paragraph(note, { bullet: { level: 0 } })),
                    ...curriculumSourceParagraphs(test.curriculumSources, translate, test.curriculumNotice)
                ]
            }
        ]
    });
}
